import { useMemo } from "react";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { resolveSearchParam } from "@hooks/searchParamRegistry";

export interface PaginatedListFilters {
  page: number;
  pageSize: number;
  search?: string;
  searchField?: string;
  fieldFilters?: Record<string, any>;
  sort?: string | null;
}

export interface PaginatedListResult<T = any> {
  items: T[];
  totalItems: number;
  totalPages: number;
  isLoading: boolean;
  error: any;
  mutate: () => void;
}

export interface PaginatedListOptions {
  responseKey?: string;
  totalElementsKey?: string;
  totalPagesKey?: string;
}

const sanitize = (fieldFilters?: Record<string, any>): Record<string, any> => {
  if (!fieldFilters) return {};
  const result: Record<string, any> = {};
  Object.entries(fieldFilters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    result[key] = value;
  });
  return result;
};

export const usePaginatedList = <T = any>(
  url: string | null,
  filters: PaginatedListFilters,
  options: PaginatedListOptions = {},
): PaginatedListResult<T> => {
  const responseKey = options.responseKey ?? "output";
  const totalElementsKey = options.totalElementsKey ?? "total_elements";
  const totalPagesKey = options.totalPagesKey ?? "total_pages";

  const fetchFilters = useMemo(() => {
    const base: Record<string, any> = {
      pageIndex: Math.max(filters.page - 1, 0),
      pageSize: filters.pageSize,
    };
    if (filters.search) {
      const param = resolveSearchParam(url, filters.searchField);
      base[param] = filters.search;
    }
    if (filters.sort) base.sort = filters.sort;
    Object.assign(base, sanitize(filters.fieldFilters));
    return base;
  }, [filters, url]);

  const { dataTable, isLoading, error, mutate } = useFetchTable<any>(
    url,
    undefined,
    fetchFilters,
  );

  const items: T[] = (dataTable?.[responseKey] as T[]) || [];
  const totalItems: number =
    dataTable?.metadata?.[totalElementsKey] ?? items.length;
  const totalPages: number =
    dataTable?.metadata?.[totalPagesKey] ??
    Math.max(Math.ceil(totalItems / filters.pageSize), 1);

  return {
    items,
    totalItems,
    totalPages,
    isLoading,
    error,
    mutate,
  };
};
